import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  PRIMARY_OUTLET,
  RouterStateSnapshot,
  TitleStrategy,
} from '@angular/router';

/**
 * NHP 2/17/2026 AI authorship note:
 * This file was created with the assistance of OpenAI GPT-5 Codex (Codex coding agent),
 * then reviewed and adapted for this codebase.
 *
 * Purpose:
 * Centralized browser-tab title formatting for the Angular SPA.
 *
 * Why this exists:
 * 1) Angular supports route-level titles via `Route.title`.
 * 2) ABP registers its own default `TitleStrategy` that formats titles as `<page> | <app>`.
 * 3) This project requires the inverse format: `SGU Web Self-Service Portal - <page>`.
 * 4) We also need one contextual title override:
 *    `faculty-profile?email=...` should display as `Preview Profile`.
 *
 * Effective formatting contract:
 * - Default: `SGU Web Self-Service Portal - <Resolved Page Title>`
 * - No resolvable page title: `SGU Web Self-Service Portal`
 *
 * Title resolution order:
 * 1) Management preview special-case (`faculty-profile` + `email` query param):
 *    => "Preview Profile"
 * 2) Angular route title (via `Route.title`) from `buildTitle(snapshot)`.
 * 3) URL-based fallback generated from route segments (humanized).
 *
 * Fallback examples:
 * - `/` => `Home`
 * - `/faculty-management` => `Faculty Management`
 *
 * ABP localization-key guard:
 * - If a resolved title looks like an ABP localization key (`::Something`),
 *   we avoid putting that raw key in the browser tab and use URL fallback instead.
 *
 * Integration requirement:
 * - Register this strategy as the final `TitleStrategy` provider in `app.config.ts`
 *   so it overrides ABP's default strategy registration from `provideAbpCore(...)`.
 *
 * Sources:
 * - Angular API (`TitleStrategy`):
 *   https://angular.dev/api/router/TitleStrategy
 * - Angular routing/title guidance:
 *   https://angular.dev/guide/routing/define-routes
 * - ABP core package showing default title strategy registration:
 *   `angular/node_modules/@abp/ng.core/fesm2022/abp-ng.core.mjs` (search for `provide: TitleStrategy`)
 */
@Injectable()
export class AppPageTitleStrategy extends TitleStrategy {
  /**
   * Base application name displayed in every tab title.
   */
  private readonly appName = 'SGU Web Self-Service Portal';

  constructor(private readonly titleService: Title) {
    super();
  }

  /**
   * Called by Angular after successful navigation.
   * Computes and writes `document.title` via Angular's `Title` service.
   */
  override updateTitle(snapshot: RouterStateSnapshot): void {
    // Identify the currently active primary route leaf.
    const activeRoute = this.getPrimaryLeafRoute(snapshot.root);

    // Faculty Management "Preview" navigates to `/faculty-profile?email=<target-user-email>`.
    // For that context, title must read "Preview Profile" instead of "Faculty Profile".
    const isManagementPreviewProfile =
      activeRoute?.routeConfig?.path === 'faculty-profile' &&
      activeRoute.queryParamMap.has('email');

    // Route title resolution precedence:
    // 1) explicit preview label
    // 2) Angular's resolved route title
    // 3) URL-segment fallback
    let pageTitle = isManagementPreviewProfile
      ? 'Preview Profile'
      : this.buildTitle(snapshot) ?? this.buildFallbackTitle(snapshot.root);

    // ABP localization keys (for example `::Menu:Home`) should not appear in tab text.
    // If we detect that pattern, replace it with a humanized fallback title.
    if (pageTitle?.startsWith('::')) {
      pageTitle = this.buildFallbackTitle(snapshot.root);
    }

    // Final format requirement for this project:
    // `SGU Web Self-Service Portal - <page title>`
    this.titleService.setTitle(pageTitle ? `${this.appName} - ${pageTitle}` : this.appName);
  }

  /**
   * Walks down the active PRIMARY_OUTLET chain and returns its leaf route
   */
  private getPrimaryLeafRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot | null {
    let current: ActivatedRouteSnapshot | null = route;

    while (current) {
      const next = current.children.find(child => child.outlet === PRIMARY_OUTLET) ?? null;
      if (!next) {
        return current;
      }
      current = next;
    }

    return null;
  }

  /**
   * Builds a readable fallback title from URL segments when no explicit route title exists.
   */
  private buildFallbackTitle(route: ActivatedRouteSnapshot): string | undefined {
    const segments: string[] = [];
    let current: ActivatedRouteSnapshot | null = route;

    while (current) {
      const next = current.children.find(child => child.outlet === PRIMARY_OUTLET) ?? null;
      if (!next) {
        break;
      }

      segments.push(...next.url.map(segment => segment.path).filter(Boolean));
      current = next;
    }

    if (!segments.length) {
      return 'Home';
    }

    return segments.map(segment => this.humanizeSegment(segment)).join(' ');
  }

  /**
   * Converts a single URL segment into title case text.
   * Example: `faculty-profile` -> `Faculty Profile`
   */
  private humanizeSegment(segment: string): string {
    return segment
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, char => char.toUpperCase());
  }
}
